import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { ArrowSwap16Regular, Edit16Regular, Link16Regular, Tag16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { ContractConcept } from '../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { ErrorType } from '../../../models/HttpError';

import ContractConceptSelectorComponent from '../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import ConceptFormComponent from '../../Contracts/Concept/ConceptForm/ConceptForm.Component';

// guía
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

export interface ConceptCardProps {
    consumption: ProtocolConsumption;
    protocolId: number;
    contractId: number | undefined;
    contractAddendumId: number | undefined;
    currentConcepts: ContractConcept[];
    currentStages: ContractStage[];
    isChangingConcept: boolean;
    failChangingConcept: boolean;
    errorChangingConcept: ErrorType | null;
    ChangeContractConcept: (protocolId: number, consumptionId: string, conceptId: number, changeAll: boolean) => void;
}

/**
 * Concepto de contrato del consumo (guía): tarjeta con el concepto asociado, "Cambiar" o
 * "Asociar" con el selector en línea y "Editar" que abre el formulario del concepto.
 */
export const ConsumptionDetailsConcept = (props: ConceptCardProps) => {
    const [t] = useTranslation();
    const c = props.consumption;

    const [editing, setEditing] = useState(false);
    const [newConcept, setNewConcept] = useState<ContractConcept | undefined>(undefined);
    const [changeAll, setChangeAll] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const wasChanging = useRef(false);

    // Al terminar el cambio (sin error) se cierra el editor.
    useEffect(() => {
        if (wasChanging.current && !props.isChangingConcept && !props.failChangingConcept) {
            setEditing(false);
            setNewConcept(undefined);
            setChangeAll(false);
        }
        wasChanging.current = props.isChangingConcept;
    }, [props.isChangingConcept, props.failChangingConcept]);

    const hasConcept = !!c.contractConceptId;
    const canEditConcept = hasConcept && !!props.contractId && props.contractAddendumId !== undefined;

    return (
        <div className={`cdt-assoc cdt-assoc--wide${editing ? ' is-editing' : ''}${hasConcept ? '' : ' is-empty'}`} data-testid="cdt-concept">
            <span className="cdt-assoc__label"><span className="cdt-assoc__icon" aria-hidden="true"><Tag16Regular /></span>{t('consumptionDetails.conceptAssociated')}</span>

            {props.failChangingConcept && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{t('consumptionDetails.conceptAssociation')}</MessageBarTitle>
                        {props.errorChangingConcept?.ErrorMessage || props.errorChangingConcept?.Errors?.join(', ')}
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {!editing ? (
                <div className="cdt-assoc__body">
                    <span className={hasConcept ? 'cdt-assoc__value' : 'cdt-assoc__empty'} title={c.conceptDescription}>
                        {c.conceptDescription ?? t('consumptionDetails.notAssociated')}
                    </span>
                    <div className="cdt-actions">
                        {/* Con concepto las dos acciones son secundarias; sin él, asociar es la única y toma el peso de primaria. */}
                        <Button appearance={hasConcept ? 'subtle' : 'primary'} size="small" icon={hasConcept ? <ArrowSwap16Regular /> : <Link16Regular />} onClick={() => setEditing(true)} disabled={!props.contractId} data-testid="cdt-concept-change">
                            {hasConcept ? t('consumptionDetails.change') : t('consumptionDetails.associateConcept')}
                        </Button>
                        {canEditConcept && (
                            <Button appearance="subtle" size="small" icon={<Edit16Regular />} onClick={() => setShowEditForm(true)} data-testid="cdt-concept-edit">
                                {t('consumptionDetails.edit')}
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                props.contractId && (
                    <div className="cdt-assoc__editor">
                        {/* Se resuelve por id contra la lista de conceptos: la descripción no casa con nada. */}
                        <ContractConceptSelectorComponent
                            ContractId={props.contractId}
                            referralDate={c.serviceDueDate}
                            onSelectedContractConcept={(concepts: ContractConcept[]) => setNewConcept(concepts?.[0])}
                            ContractConceptsSelected={c.contractConceptId ? [c.contractConceptId] : []}
                            MultiselectEnable={false}
                        />
                        <Checkbox checked={changeAll} onChange={(_event, data) => setChangeAll(data.checked === true)} label={t('consumptionDetails.applyToAllOfType')} />
                        <div className="cdt-actions">
                            <Button appearance="primary" size="small" disabled={props.isChangingConcept || !newConcept} icon={props.isChangingConcept ? <Spinner size="extra-tiny" /> : undefined} onClick={() => { if (newConcept?.id) props.ChangeContractConcept(props.protocolId, c.id, newConcept.id, changeAll); }}>
                                {t('consumptionDetails.confirm')}
                            </Button>
                            <Button appearance="subtle" size="small" disabled={props.isChangingConcept} onClick={() => { setEditing(false); setNewConcept(undefined); }}>{t('common.cancel')}</Button>
                        </div>
                    </div>
                )
            )}

            {canEditConcept && props.contractId && props.contractAddendumId !== undefined && c.contractConceptId && (
                <ConceptFormComponent
                    isOpen={showEditForm}
                    openPanel={() => setShowEditForm(true)}
                    dismissPanel={() => setShowEditForm(false)}
                    ContractId={props.contractId}
                    ContractAddendumId={props.contractAddendumId}
                    ContractConceptId={c.contractConceptId}
                    CurrentConcepts={props.currentConcepts}
                    CurrentStages={props.currentStages}
                />
            )}
        </div>
    );
};

export default ConsumptionDetailsConcept;
